import CustomSelect from '@/components/shared/CustomSelect/CustomSelect';
import { MultiComboBox } from '@/components/shared/MultiComboBox/MultiComboBox';
import { Label } from '@/components/shared/Label/Label';
import { Button } from '@/components/ui/button';
import { SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import DateRangePicker from '@/pages/Dashboard/Partials/DateRangePicker';
import { useSoldProductFilterOptions } from '@/hooks/useSoldProductFilterOptions';
import useCurrencyStore from '@/store/useCurrencyStore';
import useModalStore from '@/store/useModalStore';
import { fetchReportTemplateOptions } from '@/utils/checkoutTemplates';
import {
  AffiliateReportFilterOption,
  AffiliateReportSortBy,
} from '@/@types/AffiliateReport';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { DateRange } from 'react-day-picker';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

const FilterSchema = z.object({
  sortBy: z.enum(['sales', 'revenue', 'profit']).optional(),
  products: z
    .array(z.object({ value: z.string(), label: z.string() }))
    .optional(),
  offers: z
    .array(z.object({ value: z.string(), label: z.string() }))
    .optional(),
  affiliates: z
    .array(z.object({ value: z.string(), label: z.string() }))
    .optional(),
  checkouts: z
    .array(z.object({ value: z.string(), label: z.string() }))
    .optional(),
  templates: z
    .array(z.object({ value: z.string(), label: z.string() }))
    .optional(),
});

type FilterFormData = z.infer<typeof FilterSchema>;

export const FILTER_AFFILIATE_REPORT_MODAL = 'filter-affiliate-report';

interface Props {
  filterValues: {
    products: AffiliateReportFilterOption[];
    affiliates: AffiliateReportFilterOption[];
    offers: AffiliateReportFilterOption[];
    checkouts: AffiliateReportFilterOption[];
    templates: AffiliateReportFilterOption[];
    sortBy: AffiliateReportSortBy;
  };
  dateRange: DateRange | undefined;
  onDateRangeChange: (range: DateRange | undefined) => void;
  onFilter: (data: FilterFormData) => void;
}

export default function FilterAffiliateReport({
  filterValues,
  dateRange,
  onDateRangeChange,
  onFilter,
}: Props) {
  const { t } = useTranslation(['reports', 'common', 'sales']);
  const { closeModal } = useModalStore();
  const { currency } = useCurrencyStore();

  const {
    handleSubmit,
    setValue,
    watch,
    clearErrors,
    getValues,
  } = useForm<FilterFormData>({
    resolver: zodResolver(FilterSchema),
    defaultValues: {
      sortBy: filterValues.sortBy || 'revenue',
      products: filterValues.products || [],
      offers: filterValues.offers || [],
      affiliates: filterValues.affiliates || [],
      checkouts: filterValues.checkouts || [],
      templates: filterValues.templates || [],
    },
  });

  const {
    productIds,
    productIdsKey,
    offerIds,
    offerIdsKey,
    fetchProductOptions,
    fetchOfferOptions,
    fetchCheckoutsOptions,
    fetchAffiliatesOptions,
    handleProductsChange,
    handleOffersChange,
    queryKeys,
  } = useSoldProductFilterOptions({
    watch,
    setValue,
    getValues,
    currency,
    scope: 'affiliate-report',
    enableCheckouts: true,
    enableAffiliates: true,
    enableCoproducers: false,
  });

  const onSubmit = (data: FilterFormData) => {
    onFilter(data);
    closeModal(FILTER_AFFILIATE_REPORT_MODAL);
  };

  const sortOptions = [
    { value: 'sales', label: t('reports:affiliateReport.sort.sales') },
    { value: 'revenue', label: t('reports:affiliateReport.sort.revenue') },
    { value: 'profit', label: t('reports:affiliateReport.sort.profit') },
  ];

  return (
    <section className="h-full flex relative flex-col justify-between">
      <SheetHeader className="md:p-6 p-4">
        <SheetTitle className="flex gap-2">{t('reports:filters')}</SheetTitle>
      </SheetHeader>

      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col justify-between h-full"
      >
        <section className="flex relative flex-col gap-4 px-4 md:px-6 pb-24">
          <CustomSelect
            label={t('reports:affiliateReport.sort.label')}
            placeholder={t('reports:affiliateReport.sort.label')}
            value={watch('sortBy') || 'revenue'}
            onValueChange={(value) =>
              setValue('sortBy', value as FilterFormData['sortBy'])
            }
            options={sortOptions}
          />

          <div className="space-y-2">
            <Label>{t('sales:date_range_label')}</Label>
            <DateRangePicker
              defaultValue={dateRange}
              onChange={onDateRangeChange}
              widthFull
            />
          </div>

          <MultiComboBox
            label={t('sales:products')}
            placeholder={t('sales:products_placeholder')}
            fetchOptions={fetchProductOptions}
            values={watch('products')}
            onChange={(value) => {
              handleProductsChange(value);
              clearErrors('products');
            }}
            queryKey="affiliate-report-products"
            returnFullObject
            cypressName="multicombobox-products"
            selectAllOptions
            newParams={[currency]}
            isEditing
          />

          <MultiComboBox
            key={`offers-${productIdsKey}`}
            label={t('sales:offer')}
            placeholder={t('sales:offer_placeholder')}
            fetchOptions={fetchOfferOptions}
            values={watch('offers')}
            onChange={(values) => {
              handleOffersChange(values);
              clearErrors('offers');
            }}
            queryKey={queryKeys.offers}
            returnFullObject
            cypressName="multicombobox-offers"
            selectAllOptions
            newParams={productIds}
            disabled={productIds.length === 0}
            isEditing
          />

          <MultiComboBox
            key={`checkouts-${productIdsKey}-${offerIdsKey}`}
            label={t('reports:checkout')}
            placeholder={t('common:select_checkout')}
            fetchOptions={fetchCheckoutsOptions}
            values={watch('checkouts')}
            onChange={(value) => {
              setValue('checkouts', value);
              clearErrors('checkouts');
            }}
            queryKey={queryKeys.checkouts}
            returnFullObject
            cypressName="multicombobox-checkouts"
            selectAllOptions
            newParams={[...productIds, ...offerIds]}
            disabled={productIds.length === 0 || offerIds.length === 0}
            isEditing
          />

          <MultiComboBox
            label={t('reports:template')}
            placeholder={t('reports:select_template')}
            fetchOptions={fetchReportTemplateOptions}
            values={watch('templates')}
            onChange={(value) => {
              setValue('templates', value);
              clearErrors('templates');
            }}
            queryKey="report-templates-affiliate-report"
            returnFullObject
            cypressName="multicombobox-templates"
            selectAllOptions
            isEditing
          />

          <MultiComboBox
            key={`affiliates-${productIdsKey}`}
            label={t('sales:affiliate')}
            placeholder={t('sales:affiliate_placeholder')}
            fetchOptions={fetchAffiliatesOptions}
            values={watch('affiliates')}
            onChange={(value) => {
              setValue('affiliates', value);
              clearErrors('affiliates');
            }}
            queryKey={queryKeys.affiliates}
            returnFullObject
            cypressName="multicombobox-affiliates"
            selectAllOptions
            newParams={productIds}
            isEditing
          />
        </section>

        <SheetFooter className="flex z-50 sticky bottom-0 bg-[#09090b] w-full border-t border-t-colorBorder p-4 items-end mt-4 justify-end">
          <div className="flex w-full z-50 gap-2 justify-end items-end">
            <Button
              variant="ghost"
              className="w-full !text-colorText md:max-w-[118px]"
              onClick={() => closeModal(FILTER_AFFILIATE_REPORT_MODAL)}
              type="button"
            >
              {t('common:cancel')}
            </Button>
            <Button
              variant="default"
              className="w-full md:max-w-fit"
              type="submit"
              data-cy="apply-affiliate-report-filters"
            >
              {t('sales:apply_filters')}
            </Button>
          </div>
        </SheetFooter>
      </form>
    </section>
  );
}
